import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEAs, useGroups, useProfitCalendar } from "../hooks/useDashboardData";
import { GroupFilterBar } from "../components/GroupFilterBar";
import { StatCard } from "../components/StatCard";
import { CardSkeleton } from "../components/Skeleton";
import { formatCurrency, formatNumber } from "../lib/format";
import { filterEasByGroup } from "../lib/groups";
import type { CalendarRow, EaStatus } from "../types";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Green/red tint in three strengths, by the day's size relative to the month's biggest day.
// Written out in full because Tailwind only ships class names it can see in the source.
const PROFIT_TINT = ["bg-emerald-500/10", "bg-emerald-500/20", "bg-emerald-500/30"];
const LOSS_TINT = ["bg-rose-500/10", "bg-rose-500/20", "bg-rose-500/30"];

interface DayTotal {
  profit: number;
  trades: number;
  wins: number;
  lots: number;
  accounts: CalendarRow[];
}

/** The month containing today in Malaysia time, as "YYYY-MM" - only used to pick the starting month. */
function currentMonth(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kuala_Lumpur", year: "numeric", month: "2-digit" })
    .format(new Date())
    .slice(0, 7);
}

function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function dayKey(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function weekdayIndex(d: Date): number {
  return (d.getUTCDay() + 6) % 7; // Monday = 0
}

/** "+958", "+1.2k", "-12k" - short enough for a phone-width calendar cell. */
function compactMoney(v: number): string {
  const sign = v < 0 ? "-" : "+";
  const a = Math.abs(v);
  if (a >= 1000) return `${sign}${(a / 1000).toFixed(a >= 10_000 ? 0 : 1)}k`;
  return `${sign}${Math.round(a)}`;
}

function signedCurrency(v: number): string {
  return `${v > 0 ? "+" : ""}${formatCurrency(v)}`;
}

function eaLabel(ea: EaStatus | undefined, accountNumber: string): string {
  return ea ? ea.account_name || ea.ea_name : accountNumber;
}

export function Calendar() {
  const thisMonth = currentMonth();
  const [month, setMonth] = useState(thisMonth);
  const [groupId, setGroupId] = useState<number | null>(null);
  const [account, setAccount] = useState<string>("");
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading } = useProfitCalendar(month);
  const { data: easData } = useEAs();
  const { data: groupsData } = useGroups();
  const eas = useMemo(() => easData?.eas ?? [], [easData]);
  const groups = useMemo(() => groupsData?.groups ?? [], [groupsData]);
  const eaByAccount = useMemo(() => new Map(eas.map((ea) => [ea.account_number, ea])), [eas]);

  // Same rule as the Dashboard: a group shows its members, "All" leaves out excluded groups,
  // and a picked account overrides both.
  const inScope = useMemo(() => {
    if (account) return new Set([account]);
    return new Set(filterEasByGroup(eas, groupId, groups).map((ea) => ea.account_number));
  }, [account, eas, groupId, groups]);

  const byDay = useMemo(() => {
    const map = new Map<string, DayTotal>();
    for (const row of data?.rows ?? []) {
      if (!inScope.has(row.account_number)) continue;
      const t = map.get(row.day) ?? { profit: 0, trades: 0, wins: 0, lots: 0, accounts: [] };
      t.profit += row.profit;
      t.trades += row.trades;
      t.wins += row.wins;
      t.lots += row.lots;
      t.accounts.push(row);
      map.set(row.day, t);
    }
    return map;
  }, [data, inScope]);

  const [y, m] = month.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const today = data?.today ?? "";

  // Weekends only get columns when something actually traded on one (crypto, say), so a
  // normal month gets wider Mon-Fri cells - which matters at phone width.
  const showWeekend = [...byDay.keys()].some((d) => weekdayIndex(new Date(`${d}T00:00:00Z`)) >= 5);
  const shownWeekdays = showWeekend ? [0, 1, 2, 3, 4, 5, 6] : [0, 1, 2, 3, 4];

  // Monday-start weeks covering the month; days from the neighbouring months render blank.
  const DAY_MS = 86_400_000;
  const lastDay = Date.UTC(y, m - 1, daysInMonth);
  const weeks: Date[][] = [];
  for (let monday = first.getTime() - weekdayIndex(first) * DAY_MS; monday <= lastDay; monday += 7 * DAY_MS) {
    weeks.push(Array.from({ length: 7 }, (_, i) => new Date(monday + i * DAY_MS)));
  }

  const monthDays = [...byDay.entries()].filter(([d]) => d.startsWith(month));
  const maxAbs = Math.max(1, ...monthDays.map(([, t]) => Math.abs(t.profit)));
  const monthTotal = monthDays.reduce((s, [, t]) => s + t.profit, 0);
  const monthTrades = monthDays.reduce((s, [, t]) => s + t.trades, 0);
  const winDays = monthDays.filter(([, t]) => t.profit > 0).length;
  const best = monthDays.reduce<[string, DayTotal] | null>((b, e) => (!b || e[1].profit > b[1].profit ? e : b), null);
  const worst = monthDays.reduce<[string, DayTotal] | null>((w, e) => (!w || e[1].profit < w[1].profit ? e : w), null);

  const activeDay =
    selected && selected.startsWith(month) ? selected : today.startsWith(month) ? today : null;
  const activeTotal = activeDay ? byDay.get(activeDay) : undefined;

  const monthLabel = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric", timeZone: "UTC" }).format(first);
  const shortDate = (d: string) =>
    new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }).format(new Date(`${d}T00:00:00Z`));
  const longDate = (d: string) =>
    new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" }).format(
      new Date(`${d}T00:00:00Z`),
    );

  const accountOptions = [...eas].sort((a, b) => eaLabel(a, a.account_number).localeCompare(eaLabel(b, b.account_number)));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <GroupFilterBar
          groups={groups}
          // -1 matches no chip, so none lights up while a single account is picked.
          value={account ? -1 : groupId}
          onChange={(id) => {
            setGroupId(id);
            setAccount("");
          }}
          totalCount={filterEasByGroup(eas, null, groups).length}
        />
        <select
          value={account}
          onChange={(e) => setAccount(e.target.value)}
          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-200 outline-none focus:ring-2 focus:ring-emerald-500/50 sm:w-64"
          aria-label="Show a single account"
        >
          <option value="">All accounts in the group above</option>
          {accountOptions.map((ea) => (
            <option key={ea.account_number} value={ea.account_number}>
              {eaLabel(ea, ea.account_number)} ({ea.account_number})
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={() => setMonth(shiftMonth(month, -1))}
          className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h2 className="text-base font-semibold text-slate-100">{monthLabel}</h2>
        <button
          onClick={() => setMonth(shiftMonth(month, 1))}
          disabled={month >= thisMonth}
          className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
          aria-label="Next month"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {isLoading || !data ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <StatCard
              label="Month P/L"
              value={signedCurrency(monthTotal)}
              subtext={`${formatNumber(monthTrades, 0)} trades`}
              tone={monthTotal > 0 ? "positive" : monthTotal < 0 ? "negative" : "default"}
            />
            <StatCard
              label="Winning days"
              value={`${winDays} / ${monthDays.length}`}
              subtext={monthDays.length ? `${Math.round((winDays / monthDays.length) * 100)}% of trading days` : undefined}
            />
            <StatCard
              label="Best day"
              value={best ? signedCurrency(best[1].profit) : "-"}
              subtext={best ? shortDate(best[0]) : undefined}
              tone={best && best[1].profit > 0 ? "positive" : "default"}
            />
            <StatCard
              label="Worst day"
              value={worst ? signedCurrency(worst[1].profit) : "-"}
              subtext={worst ? shortDate(worst[0]) : undefined}
              tone={worst && worst[1].profit < 0 ? "negative" : "default"}
            />
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-2 sm:p-4">
            <div
              className="grid gap-1 sm:gap-2"
              style={{ gridTemplateColumns: `repeat(${shownWeekdays.length}, minmax(0, 1fr)) minmax(0, 1.1fr)` }}
            >
              {shownWeekdays.map((w) => (
                <div key={w} className="pb-1 text-center text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  {WEEKDAYS[w]}
                </div>
              ))}
              <div className="pb-1 text-center text-[11px] font-medium uppercase tracking-wide text-slate-500">Week</div>

              {weeks.map((week) => {
                const weekTotal = week
                  .filter((d) => d.getUTCMonth() === m - 1)
                  .reduce((s, d) => s + (byDay.get(dayKey(d))?.profit ?? 0), 0);
                const weekTraded = week.some((d) => d.getUTCMonth() === m - 1 && byDay.has(dayKey(d)));
                return [
                  ...shownWeekdays.map((w) => {
                    const d = week[w];
                    const key = dayKey(d);
                    if (d.getUTCMonth() !== m - 1) return <div key={key} />;
                    const total = byDay.get(key);
                    const isFuture = today !== "" && key > today;
                    const level = total ? Math.min(2, Math.floor((Math.abs(total.profit) / maxAbs) * 3)) : 0;
                    const tint = total ? (total.profit >= 0 ? PROFIT_TINT[level] : LOSS_TINT[level]) : "";
                    return (
                      <button
                        key={key}
                        onClick={() => setSelected(key)}
                        disabled={isFuture}
                        className={`flex min-h-14 flex-col items-start justify-between rounded-lg border p-1.5 text-left transition-colors sm:min-h-20 sm:p-2 ${tint} ${
                          key === activeDay
                            ? "border-emerald-400"
                            : key === today
                              ? "border-emerald-500/40"
                              : "border-slate-800 hover:border-slate-600"
                        } ${isFuture ? "opacity-40" : ""}`}
                      >
                        <span className="flex w-full items-center justify-between text-[11px] text-slate-400">
                          {d.getUTCDate()}
                          {key === today && <span className="hidden text-[10px] text-emerald-400 sm:inline">Today</span>}
                        </span>
                        {total && (
                          <span
                            className={`w-full truncate text-xs font-semibold tabular-nums sm:text-sm ${
                              total.profit >= 0 ? "text-emerald-400" : "text-rose-400"
                            }`}
                          >
                            <span className="sm:hidden">{compactMoney(total.profit)}</span>
                            <span className="hidden sm:inline">{signedCurrency(total.profit)}</span>
                          </span>
                        )}
                      </button>
                    );
                  }),
                  <div
                    key={`week-${dayKey(week[0])}`}
                    className="flex min-h-14 flex-col items-end justify-center rounded-lg bg-slate-950/60 p-1.5 sm:min-h-20 sm:p-2"
                  >
                    {weekTraded && (
                      <span
                        className={`w-full truncate text-right text-xs font-semibold tabular-nums sm:text-sm ${
                          weekTotal >= 0 ? "text-emerald-400" : "text-rose-400"
                        }`}
                      >
                        <span className="sm:hidden">{compactMoney(weekTotal)}</span>
                        <span className="hidden sm:inline">{signedCurrency(weekTotal)}</span>
                      </span>
                    )}
                  </div>,
                ];
              })}
            </div>
          </div>

          <DayDetail
            day={activeDay}
            isToday={activeDay === today}
            total={activeTotal}
            label={activeDay ? longDate(activeDay) : ""}
            eaByAccount={eaByAccount}
          />
        </>
      )}
    </div>
  );
}

function DayDetail({
  day,
  isToday,
  total,
  label,
  eaByAccount,
}: {
  day: string | null;
  isToday: boolean;
  total: DayTotal | undefined;
  label: string;
  eaByAccount: Map<string, EaStatus>;
}) {
  if (!day) {
    return <p className="text-center text-sm text-slate-500">Tap a day to see each account's result.</p>;
  }

  const accounts = [...(total?.accounts ?? [])].sort((a, b) => b.profit - a.profit);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/60">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-800 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-100">{label}</h3>
          {isToday && <p className="text-xs text-slate-500">Live - the same figure as Profit Today on the Dashboard.</p>}
        </div>
        {total && (
          <span className={`text-sm font-semibold tabular-nums ${total.profit >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
            {signedCurrency(total.profit)}
          </span>
        )}
      </div>
      {accounts.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-slate-500">No closed trades on this day.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[420px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2">Account</th>
                <th className="px-4 py-2 text-right">Trades</th>
                <th className="px-4 py-2 text-right">Win rate</th>
                <th className="px-4 py-2 text-right">Lots</th>
                <th className="px-4 py-2 text-right">P/L</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {accounts.map((row) => (
                <tr key={row.account_number}>
                  <td className="px-4 py-2 text-slate-200">{eaLabel(eaByAccount.get(row.account_number), row.account_number)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-300">{row.trades}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-300">
                    {row.trades ? `${Math.round((row.wins / row.trades) * 100)}%` : "-"}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-300">{formatNumber(row.lots)}</td>
                  <td
                    className={`px-4 py-2 text-right font-medium tabular-nums ${row.profit >= 0 ? "text-emerald-400" : "text-rose-400"}`}
                  >
                    {signedCurrency(row.profit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
